import { Link } from 'react-router-dom';
import { Hash, Lock, Volume2, Plus, Trash2, Activity, BellOff } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { GuildChannelSummary } from '@/lib/api';
// Imported by direct path, not through `chat/components/index` — see the barrel's
// note on the TDZ cycle that re-exporting into other arches reopens.
import { UnreadBadges } from '../../chat/components/UnreadBadges';

interface GuildChannelSidebarProps {
  channels: GuildChannelSummary[];
  loading: boolean;
  /** Fetch failure — rendered instead of the (empty) channel list so a broken
   * fetch doesn't masquerade as a guild with no channels. */
  error?: string | null;
  /** True when the caller can manage channels (guild owner/admin). */
  canManage: boolean;
  /** True when the in-page "#activity" system view is selected. */
  activityActive: boolean;
  onSelectActivity: () => void;
  /**
   * Opens a text channel in the page's centre pane (Discord-style). When
   * omitted the rail falls back to linking out to the chat arche.
   */
  onSelectChannel?: (channel: GuildChannelSummary) => void;
  /** Highlights the channel currently shown in the centre pane. */
  activeChannelId?: string | null;
  onAddChannel: () => void;
  onDeleteChannel: (channel: GuildChannelSummary) => void;
}

/**
 * Discord/Slack-style channel rail for a guild. The read-only "#activity"
 * system entry is pinned at the top and selects an in-page view; every text
 * channel links out to the Chat arche (chat is a separate arche). Admins get a
 * "+" to create channels and a hover delete affordance per channel.
 */
export function GuildChannelSidebar({
  channels,
  loading,
  error,
  canManage,
  activityActive,
  onSelectActivity,
  onSelectChannel,
  activeChannelId,
  onAddChannel,
  onDeleteChannel,
}: GuildChannelSidebarProps) {
  return (
    <nav className="flex flex-col gap-0.5" aria-label="Guild channels">
      <div className="flex items-center justify-between px-2 pb-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Channels
        </span>
        {canManage && (
          <button
            type="button"
            onClick={onAddChannel}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Create channel"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Read-only system activity channel — selects an in-page view. */}
      <button
        type="button"
        onClick={onSelectActivity}
        className={cn(
          'group flex items-center gap-1.5 rounded px-2 py-1.5 text-sm w-full text-left',
          activityActive
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )}
      >
        <Activity className="h-4 w-4 flex-shrink-0" />
        <span className="truncate">activity</span>
      </button>

      {loading ? (
        <div className="space-y-1 px-2 py-1">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </div>
      ) : error ? (
        <p className="px-2 py-1 text-xs text-destructive">{error}</p>
      ) : (
        channels.map((channel) => (
          <div key={channel.channelId} className="group relative flex items-center">
            {(() => {
              const isActive = channel.channelId === activeChannelId;
              // The open channel is being continuously marked read by the chat
              // pane beside it, so its badge is always stale-high by exactly the
              // messages the user is looking at. Suppress rather than poll for a
              // zero we already know.
              const unread = isActive ? 0 : channel.unreadCount;
              const hasMention = isActive ? false : channel.mentionCount > 0;
              const glyph = channel.isPrivate ? (
                <Lock className="h-4 w-4 flex-shrink-0" />
              ) : channel.isReadOnly ? (
                <Volume2 className="h-4 w-4 flex-shrink-0" />
              ) : (
                <Hash className="h-4 w-4 flex-shrink-0" />
              );
              const className = cn(
                'flex flex-1 items-center gap-1.5 rounded px-2 py-1.5 text-sm min-w-0 text-left',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : unread > 0
                    ? // Unread outranks the resting muted tone: an unread channel
                      // reads as active even before the eye reaches the badge.
                      'font-medium text-foreground hover:bg-accent/50'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
              );
              const body = (
                <>
                  {glyph}
                  <span className="truncate">{channel.name ?? 'channel'}</span>
                  {channel.isMuted && (
                    <BellOff aria-label="muted" className="h-3 w-3 flex-shrink-0 opacity-60" />
                  )}
                  {/* Trailing so the badges sit at the rail's right edge; the
                      delete affordance overlays them on hover, which is fine —
                      a channel you're about to delete needs no unread count. */}
                  <span className="ml-auto flex items-center gap-1">
                    <UnreadBadges unread={unread} hasMention={hasMention} />
                  </span>
                </>
              );
              // In-page selection when the host provides it; otherwise keep the
              // original link-out so other callers don't lose navigation.
              return onSelectChannel ? (
                <button
                  type="button"
                  onClick={() => onSelectChannel(channel)}
                  className={className}
                >
                  {body}
                </button>
              ) : (
                <Link to={`/a/arche_chat/channel/${channel.channelId}`} className={className}>
                  {body}
                </Link>
              );
            })()}
            {canManage && (
              <button
                type="button"
                onClick={() => onDeleteChannel(channel)}
                // `hidden` alone would make the button unreachable by keyboard
                // (display:none can't take focus) — reveal it while anything in
                // the row, including the button itself, holds focus.
                className="absolute right-1 hidden rounded p-1 text-muted-foreground hover:text-destructive group-hover:block group-focus-within:block focus:block"
                aria-label={`Delete ${channel.name ?? 'channel'}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))
      )}

      {!loading && !error && channels.length === 0 && (
        <p className="px-2 py-1 text-xs text-muted-foreground">No channels yet.</p>
      )}
    </nav>
  );
}
